import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Field, Form, FormElement } from '@progress/kendo-react-form'
import { GridCellProps } from '@progress/kendo-react-grid'
import { DefaultButton, FormDropDownList, MessageAlert, Spinner } from 'components'
import SAGrid from 'components/Grid/SAGrid'
import { focusFirstError, getErrors } from 'core/helpers'
import { V1ApiIntegrationsClient } from 'core/http-common'
import toast from 'core/toast'
import { RequiredValidator } from 'core/Validators'
import { JenkinsJobInfoDto, TriggerJenkinsBuildDto } from 'generated/v1/v1IntegrationsApi'
import { useEffect, useState } from 'react'
import { Col, Row, Tab, Tabs } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { endLoadingAction, startLoadingAction } from 'state/actions/appActions'
import { AppDispatch } from 'state/store'
import { AlertMessage, ICustomColumn, IDropDownValue, IRootState } from 'types'

interface IFormState {
    app: IDropDownValue,
    section: IDropDownValue,
    target: IDropDownValue,
    serverType: IDropDownValue
}

const TestRailsPage = () => {
    const dispatch = useDispatch<AppDispatch>()
    const [selectedSections, setSelectedSections] = useState<Array<IDropDownValue>>()
    const [selectedServerTypes, setSelectedServerTypes] = useState<Array<IDropDownValue>>()
    const [alert, setAlert] = useState<AlertMessage>()
    const [fetchBuildsLoading, setFetchBuildsLoading] = useState(false)
    const [submitLoading, setSubmitLoading] = useState(false)

    const [experimentalBuilds, setExperimentalBuilds] = useState<JenkinsJobInfoDto[]>()
    const [qaBuilds, setQaBuilds] = useState<JenkinsJobInfoDto[]>()
    const [productionBuilds, setProductionBuilds] = useState<JenkinsJobInfoDto[]>()
    const [queuedBuilds, setQueuedBuilds] = useState<JenkinsJobInfoDto[]>()
    const [activeTab, setActiveTab] = useState('tabQueued')
    const apps = [
        { id: 1, text: 'CMS' },
        { id: 2, text: 'DCS' }
    ] as Array<IDropDownValue>

    const targets = [
        { id: 1, text: 'QA' },
        { id: 2, text: 'PRODUCTION' },
        { id: 3, text: 'EXPERIMENTAL' },
    ] as Array<IDropDownValue>

    const cmsServerTypes = [
        { id: 0, text: 'DEFAULT' },
    ] as Array<IDropDownValue>

    const cmsSections = [
        { id: 1, text: 'Core' },
    ]

    const dcsServerTypes = [
        { id: 0, text: 'DEFAULT' },
        { id: 1, text: 'ODD' },
        { id: 2, text: 'EVEN' },
    ] as Array<IDropDownValue>

    const dcsSections = [
        { id: 1, text: 'ALL (Full Regression)' },
        { id: 2, text: 'Core' },
        { id: 3, text: 'Inventory' },
        { id: 4, text: 'Leads' },
        { id: 5, text: 'Website' },
        { id: 6, text: 'Reputation' },
        { id: 7, text: 'Calls' },
        { id: 8, text: 'Pricing Tools' },
        { id: 9, text: 'Public Site' },
        { id: 10, text: 'Reports' },
        { id: 11, text: 'Settings' },
    ] as Array<IDropDownValue>

    const JenkinsUrlCell = (props: GridCellProps) => {
        const openInNewTab = (url: string) => {
            window.open(url, '_blank', 'noopener,noreferrer')
        }

        if (!props.dataItem.jenkinsUrl) return <td></td>
        return (
            <td className='text-center cursor-pointer' style={{ width: '80px' }} title='Open Jenkins URL'>
                <a
                    className='btn btn-default btn-sm gridButton'
                    role='button'
                    aria-disabled='true'
                    onClick={() => openInNewTab(props.dataItem.jenkinsUrl)}
                >
                    <FontAwesomeIcon icon={['fas', 'up-right-from-square']} />
                </a>
            </td>
        )
    }

    const ResultCell = (props: GridCellProps) => {
        if (!props.dataItem.result) return <td></td>

        let badge
        switch (props.dataItem.result) {
            case 'SUCCESS':
                badge = 'bg-success'
                break
            case 'FAILURE':
                badge = 'bg-danger'
                break
            case 'RUNNING':
                badge = 'bg-primary'
                break
            case 'QUEUED':
                badge = 'bg-primary'
                break
            default:
                badge = 'bg-secondary'
                break
        }

        return (
            <td className='text-center' style={{ width: '80px' }}>
                <span className={`badge ${badge}`}>
                    {props.dataItem.result}
                </span>
            </td>
        )
    }

    const columns: ICustomColumn[] = [
        { field: 'buildNumber', title: 'Build #' },
        { field: 'timestamp', title: 'Timestamp', filter: 'date', format: '{0:G}' },
        { field: 'app', title: 'App' },
        { field: 'section', title: 'Section' },
        { field: 'target', title: 'Target' },
        { field: 'serverType', title: 'Server Type' },
        { field: 'result', title: 'Result', cell: ResultCell },
        { field: 'jenkinsUrl', title: 'Jenkins URL', cell: JenkinsUrlCell }
    ]

    const queuedColumns: ICustomColumn[] = [
        { field: 'app', title: 'App' },
        { field: 'section', title: 'Section' },
        { field: 'target', title: 'Target' },
        { field: 'serverType', title: 'Server Type' },
    ]

    const fetchBuilds = async () => {
        try {
            setFetchBuildsLoading(true)
            const result = await V1ApiIntegrationsClient.getLast30Jobs()

            setExperimentalBuilds(result.experimentalBuilds)
            setQaBuilds(result.qaBuilds)
            setProductionBuilds(result.productionBuilds)
            setQueuedBuilds(result.queuedBuilds)
        }
        catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        }
        finally {
            setFetchBuildsLoading(false)
        }
    }

    useEffect(() => {
        fetchBuilds()
    }, [])

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSubmit = async (data: IFormState) => {
        try {

            dispatch(startLoadingAction())
            setSubmitLoading(true)
            await V1ApiIntegrationsClient.triggerJenkinsAutomationTests(
                new TriggerJenkinsBuildDto({
                    app: data.app.text,
                    section: data.section.text,
                    target: data.target.text,
                    serverType: data.serverType.text
                })
            )
            toast.success('Triggered Jenkins Automation Tests Successfully.')
            fetchBuilds()
        } catch (e) {
            toast.responseErrors(e)
        }
        finally {
            dispatch(endLoadingAction())
            setSubmitLoading(false)
        }
    }

    const handleTabSelect = (key: string | null, e: React.SyntheticEvent<unknown>) => {
        setActiveTab(key!)
    }

    return (
        <Row>
            <Col>
                <MessageAlert alert={alert} />
                <Row>
                    <Col md={6} className='mx-auto'>
                        <Form
                            onSubmit={(e) => onSubmit(e as IFormState)}
                            render={(formRenderProps) => (
                                <FormElement>
                                    <Field
                                        name='app'
                                        component={FormDropDownList}
                                        label='App:'
                                        data={apps}
                                        textField='text'
                                        dataItemKey='id'
                                        onChange={(e) => {
                                            formRenderProps.onChange('section', { value: null })
                                            formRenderProps.onChange('serverType', { value: null })
                                            formRenderProps.onChange('target', { value: null })
                                            if (!e.value) {
                                                setSelectedSections(undefined)
                                                setSelectedServerTypes(undefined)
                                                return
                                            }
                                            setSelectedSections(e.value.id === 1 ? cmsSections : dcsSections)
                                            setSelectedServerTypes(e.value.id === 1 ? cmsServerTypes : dcsServerTypes)
                                        }}
                                        required
                                        validator={RequiredValidator}
                                    />

                                    <Field
                                        name='section'
                                        component={FormDropDownList}
                                        label='Section:'
                                        data={selectedSections}
                                        textField='text'
                                        dataItemKey='id'
                                        disabled={!selectedSections || selectedSections.length === 0}
                                        required
                                        validator={RequiredValidator}
                                    />

                                    <Field
                                        name='target'
                                        component={FormDropDownList}
                                        label='Target:'
                                        data={targets}
                                        textField='text'
                                        dataItemKey='id'
                                        required
                                        validator={RequiredValidator}
                                    />

                                    <Field
                                        name='serverType'
                                        component={FormDropDownList}
                                        label='Server Type:'
                                        data={selectedServerTypes}
                                        textField='text'
                                        dataItemKey='id'
                                        disabled={!selectedSections || selectedSections.length === 0}
                                        required
                                        validator={RequiredValidator}
                                    />

                                    <div className='mt-4'>
                                        <DefaultButton
                                            data-cy='login-submit'
                                            className='btn btn-primary w-100'
                                            type='submit'
                                            disabled={submitLoading}
                                            loading={submitLoading}
                                            onClick={() => focusFirstError(formRenderProps)}>
                                            Run Tests
                                        </DefaultButton>
                                    </div>
                                </FormElement>
                            )}
                        />
                    </Col>
                </Row>

                <hr />
                <Row className='align-items-center'>
                    <Col>
                        <h3>Last 10 Builds</h3>
                    </Col>
                    <Col className='text-end'>
                        <DefaultButton
                            variant='default me-2'
                            onClick={fetchBuilds}
                        >
                            <FontAwesomeIcon icon={['fas', 'sync']} /> {' '} Refresh
                        </DefaultButton>
                    </Col>
                </Row>
                <Row>
                    <Col className='mt-4'>
                        {fetchBuildsLoading ? (
                            <Spinner center />
                        ) : (
                            <Tabs activeKey={activeTab} onSelect={handleTabSelect} id='test-rails-tabs'>
                                <Tab eventKey='tabQueued' title={`Queued (${queuedBuilds?.length})`}>
                                    <SAGrid
                                        columns={queuedColumns}
                                        defaultSort={[]}
                                        features={{ sortable: false }}
                                        data={queuedBuilds}
                                    />
                                </Tab>
                                <Tab eventKey='tabExperimental' title='Experimental'>
                                    <SAGrid
                                        columns={columns}
                                        defaultSort={[]}
                                        features={{ sortable: false }}
                                        data={experimentalBuilds}
                                    />
                                </Tab>
                                <Tab eventKey='tabQa' title='QA'>
                                    <SAGrid
                                        columns={columns}
                                        defaultSort={[]}
                                        features={{ sortable: false }}
                                        data={qaBuilds}
                                    />
                                </Tab>
                                <Tab eventKey='tabProduction' title='Production'>
                                    <SAGrid
                                        columns={columns}
                                        defaultSort={[]}
                                        features={{ sortable: false }}
                                        data={productionBuilds}
                                    />
                                </Tab>
                            </Tabs>
                        )}
                    </Col>
                </Row>
            </Col >
        </Row >
    )
}
export default TestRailsPage